import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { useSuggestionsForArtifact } from '../../store/suggestions';
import type { Artifact, Suggestion } from '../../store/types';
import { MIME_TO_ARTIFACT_TYPE } from '../../store/wire';
import InlineEditPair from './primitives/inline-edit-pair';
import PatchCard from './primitives/patch-card';
import ProseDiff from './primitives/prose-diff';
import { diffField } from './utils/diff-field';
import { pickNoteKey } from './utils/note-key';
type Props = {
  taskId: string;
  artifact: Artifact;
};
const ArtifactSuggestions = t0 => {
  const $ = _c(7);
  const {
    taskId,
    artifact
  } = t0;
  const suggestions = useSuggestionsForArtifact(taskId, artifact.id);
  if (suggestions.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== artifact || $[1] !== suggestions) {
    let t2;
    if ($[3] !== artifact) {
      t2 = s => <ArtifactPatchCard key={s.id} suggestion={s} artifact={artifact} />;
      $[3] = artifact;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = suggestions.map(t2);
    $[0] = artifact;
    $[1] = suggestions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex flex-col gap-2 mt-2">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const ArtifactPatchCard = t0 => {
  const $ = _c(50);
  const {
    suggestion,
    artifact
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.suggestion.artifact"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.canvas.suggestion.labels"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tLabels
  } = useTranslation("agents", t2);
  const op = suggestion.operation;
  const isStale = !suggestion.canApply;
  if (op.kind === "remove_task_artifact") {
    const t3 = suggestion.id;
    let t4;
    if ($[2] !== t) {
      t4 = t(_temp);
      $[2] = t;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== artifact.name || $[5] !== t) {
      t5 = t(_temp2, {
        name: artifact.name
      });
      $[4] = artifact.name;
      $[5] = t;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t5) {
      t6 = <p className="text-caption-3 text-tertiary">{t5}</p>;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== isStale || $[10] !== suggestion.id || $[11] !== t4 || $[12] !== t6) {
      t7 = <PatchCard suggestionId={t3} note={t4} isStale={isStale}>{t6}</PatchCard>;
      $[9] = isStale;
      $[10] = suggestion.id;
      $[11] = t4;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  if (op.kind === "update_task_artifact") {
    const t3 = op.name || undefined;
    let t4;
    if ($[14] !== artifact.name || $[15] !== t3) {
      t4 = diffField(artifact.name, t3);
      $[14] = artifact.name;
      $[15] = t3;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    const nameChange = t4;
    const mimeNext = op.mimeType ? MIME_TO_ARTIFACT_TYPE[op.mimeType] : undefined;
    let t5;
    if ($[17] !== artifact.type || $[18] !== mimeNext) {
      t5 = diffField(artifact.type, mimeNext);
      $[17] = artifact.type;
      $[18] = mimeNext;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    const mimeChange = t5;
    let t6;
    if ($[20] !== artifact.spec || $[21] !== op.prompt) {
      t6 = diffField(artifact.spec, op.prompt);
      $[20] = artifact.spec;
      $[21] = op.prompt;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    const promptChange = t6;
    const t7 = !!nameChange;
    const t8 = !!mimeChange;
    const t9 = !!promptChange;
    let t10;
    if ($[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
      t10 = pickNoteKey({
        renamed: t7,
        changedMime: t8,
        refinedSpec: t9
      }, "refinedAll" as const);
      $[23] = t7;
      $[24] = t8;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    const noteKey = t10;
    if (!noteKey) {
      return null;
    }
    const t11 = suggestion.id;
    let t12;
    if ($[27] !== noteKey) {
      t12 = $_1 => $_1[noteKey];
      $[27] = noteKey;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== t || $[30] !== t12) {
      t13 = t(t12);
      $[29] = t;
      $[30] = t12;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== nameChange || $[33] !== tLabels) {
      t14 = nameChange && <InlineEditPair label={tLabels(_temp3)} current={nameChange.from} suggested={nameChange.to} />;
      $[32] = nameChange;
      $[33] = tLabels;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    let t15;
    if ($[35] !== mimeChange || $[36] !== tLabels) {
      t15 = mimeChange && <InlineEditPair label={tLabels(_temp4)} current={mimeChange.from} suggested={mimeChange.to} />;
      $[35] = mimeChange;
      $[36] = tLabels;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    let t16;
    if ($[38] !== promptChange || $[39] !== tLabels) {
      t16 = promptChange && <div className="flex flex-col gap-0.5"><span className="text-caption-3 text-tertiary">{tLabels(_temp5)}:</span><ProseDiff current={promptChange.from} suggested={promptChange.to} /></div>;
      $[38] = promptChange;
      $[39] = tLabels;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== t14 || $[42] !== t15 || $[43] !== t16) {
      t17 = <div className="flex flex-col gap-2">{t14}{t15}{t16}</div>;
      $[41] = t14;
      $[42] = t15;
      $[43] = t16;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    let t18;
    if ($[45] !== isStale || $[46] !== suggestion.id || $[47] !== t13 || $[48] !== t17) {
      t18 = <PatchCard suggestionId={t11} note={t13} isStale={isStale} showDiffToggle={true}>{t17}</PatchCard>;
      $[45] = isStale;
      $[46] = suggestion.id;
      $[47] = t13;
      $[48] = t17;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    return t18;
  }
  return null;
};
export default ArtifactSuggestions;
function _temp($) {
  return $.remove;
}
function _temp2($_0) {
  return $_0.removeBody;
}
function _temp3($_2) {
  return $_2.name;
}
function _temp4($_3) {
  return $_3.type;
}
function _temp5($_4) {
  return $_4.spec;
}